import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { filter, distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import { MenuItemDto, MenuNode } from '../models/menu.model';
import { AuthService } from '../auth/auth.service';

/**
 * MenuService — single source of truth for sidebar menu state.
 *
 * Loading rules:
 *   - Menus load after login, after company switch (normal users), and after
 *     module switch.
 *   - SYSADMIN / ADMINISTRATOR: CompCode is passed but the API ignores it
 *     for filtering (reads MENUS, no CompCode/UserName filter).
 *   - Normal users: API filters User_Menus by CompCode + authenticated
 *     UserName + ModuleName.
 *   - Logout clears all menu state immediately.
 *   - An empty API response shows nothing — no hardcoded fallback ever.
 *
 * Navigation contract:
 *   - routePath from the API is the ONLY source of Angular navigation.
 *   - No menu-name-to-route mapping exists in this service.
 *   - Changing DisplayName or MenuName in MENUS does NOT require any
 *     Angular code change.
 */
@Injectable({ providedIn: 'root' })
export class MenuService implements OnDestroy {

  // ── Active module ─────────────────────────────────────────────────────────
  private readonly moduleNameSubject = new BehaviorSubject<string>('PAYROLL');
  public  readonly moduleName$ = this.moduleNameSubject.asObservable();

  // ── Menu tree state ────────────────────────────────────────────────────────
  private readonly menuNodesSubject = new BehaviorSubject<MenuNode[]>([]);
  public  readonly menuNodes$: Observable<MenuNode[]> = this.menuNodesSubject.asObservable();

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  public  readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();

  private companySub?: Subscription;
  private userSub?:    Subscription;

  constructor(
    private readonly http:        HttpClient,
    private readonly authService: AuthService,
  ) {
    // Clear menus immediately when the user logs out (currentUser$ → null).
    this.userSub = this.authService.currentUser$.subscribe(user => {
      if (!user) this.clearMenus();
    });

    // Auto-reload when the selected company or module changes —
    // but only while the user is authenticated.
    this.companySub = combineLatest([
      this.authService.selectedCompany$,
      this.moduleNameSubject,
    ]).pipe(
      filter(() => this.authService.isAuthenticated()),
      distinctUntilChanged(
        ([prevComp, prevMod], [nextComp, nextMod]) =>
          prevComp?.compCode === nextComp?.compCode && prevMod === nextMod
      ),
    ).subscribe(([company, moduleName]) => {
      this.loadMenus(company?.compCode ?? '', moduleName);
    });
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
    this.companySub?.unsubscribe();
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Switch the active module — combineLatest will reload menus automatically. */
  setModule(moduleName: string): void {
    this.moduleNameSubject.next(moduleName);
  }

  /** Key of the request currently in flight — used to de-dupe concurrent calls. */
  private inFlightKey: string | null = null;

  /**
   * Explicitly reload menus for a given company + module.
   * Called after login, company switch, or module switch.
   * De-duplicates: if an identical request is already in flight it is skipped,
   * so the reactive subscription and an explicit sidebar call don't both fire.
   */
  loadMenus(compCode: string, moduleName?: string): void {
    const mod = moduleName ?? this.moduleNameSubject.value;
    const key = `${compCode}|${mod}`;
    if (this.inFlightKey === key) return;   // identical request already running
    this.inFlightKey = key;

    const params = new HttpParams()
      .set('compCode',   compCode)
      .set('moduleName', mod);

    this.loadingSubject.next(true);
    this.http
      .get<ApiResponse<MenuItemDto[]>>(
        `${environment.apiUrl}/menu/current-user`,
        { params }
      )
      .subscribe({
        next: res => {
          this.inFlightKey = null;
          this.loadingSubject.next(false);
          const items = (res.success && res.data) ? res.data : [];
          this.menuNodesSubject.next(this.buildTree(items));
        },
        error: () => {
          this.inFlightKey = null;
          this.loadingSubject.next(false);
          // On error show nothing — never fall back to hardcoded menus.
          this.menuNodesSubject.next([]);
        },
      });
  }

  /** Clear all menu state immediately (on logout). */
  clearMenus(): void {
    this.inFlightKey = null;
    this.menuNodesSubject.next([]);
    this.loadingSubject.next(false);
  }

  // ── Action-level permission helpers ──────────────────────────────────────────
  //
  // Single source of truth for menu action (create/update/delete) permissions,
  // so feature components (Company, Category) never duplicate role or lookup
  // logic. Permissions are read from the current menu state (loaded from the
  // Menu API) and matched by routePath — the same stable navigation key the
  // sidebar uses. Only SYSADMIN gets unconditional full access (via
  // AuthService.isSysAdmin()); ADMINISTRATOR follows its configured menu rights.

  /**
   * Finds a menu node in the current menu tree by its routePath.
   * Search is recursive (menus are a nested tree) and case-insensitive on the
   * route. Returns null when no menu with that route is present in the state.
   */
  getMenuByRoute(routePath: string): MenuNode | null {
    return this.findByRoute(this.menuNodesSubject.value, routePath);
  }

  /** True when the current user may CREATE on the given menu route. */
  canCreate(routePath: string): boolean {
    if (this.authService.isSysAdmin()) return true;
    return this.getMenuByRoute(routePath)?.menuCreate ?? false;
  }

  /** True when the current user may UPDATE on the given menu route. */
  canUpdate(routePath: string): boolean {
    if (this.authService.isSysAdmin()) return true;
    return this.getMenuByRoute(routePath)?.menuUpdate ?? false;
  }

  /** True when the current user may DELETE on the given menu route. */
  canDelete(routePath: string): boolean {
    if (this.authService.isSysAdmin()) return true;
    return this.getMenuByRoute(routePath)?.menuDelete ?? false;
  }

  // ── Reactive permission streams ─────────────────────────────────────────────
  //
  // The synchronous can* helpers above snapshot whatever menu state exists at
  // call time. On a FULL PAGE REFRESH the menu tree is fetched asynchronously,
  // so a component that reads a snapshot in ngOnInit would see an empty tree and
  // resolve every action to `false` (buttons disabled). These observable
  // variants re-emit whenever the menu state changes, so components can keep
  // their permission flags in sync once menus finish loading after a refresh.
  // Only SYSADMIN always emits `true`; ADMINISTRATOR follows its menu rights.

  /** Emits whether the user may CREATE on the route, updating as menus load. */
  canCreate$(routePath: string): Observable<boolean> {
    return this.permissionStream(routePath, n => n.menuCreate);
  }

  /** Emits whether the user may UPDATE on the route, updating as menus load. */
  canUpdate$(routePath: string): Observable<boolean> {
    return this.permissionStream(routePath, n => n.menuUpdate);
  }

  /** Emits whether the user may DELETE on the route, updating as menus load. */
  canDelete$(routePath: string): Observable<boolean> {
    return this.permissionStream(routePath, n => n.menuDelete);
  }

  /**
   * Builds a boolean stream for a single menu action, recomputed on every
   * menu-state change. Privileged users always resolve to true; otherwise the
   * flag is read from the matching menu node (false when the menu is absent).
   */
  private permissionStream(
    routePath: string,
    pick: (node: MenuNode) => boolean,
  ): Observable<boolean> {
    return this.menuNodes$.pipe(
      map(nodes => {
        if (this.authService.isSysAdmin()) return true;
        const node = this.findByRoute(nodes, routePath);
        return node ? pick(node) : false;
      }),
      distinctUntilChanged(),
    );
  }

  /** Recursive depth-first search of the menu tree by routePath. */
  private findByRoute(nodes: MenuNode[], routePath: string): MenuNode | null {
    for (const node of nodes) {
      if (node.routePath && node.routePath.toLowerCase() === routePath.toLowerCase()) {
        return node;
      }
      const found = this.findByRoute(node.children, routePath);
      if (found) return found;
    }
    return null;
  }

  // ── Tree building ──────────────────────────────────────────────────────────

  /**
   * Converts the flat sorted MenuItemDto list into a recursive MenuNode tree.
   * Hierarchy is driven by MenuId dot-notation:
   *   "1"       → depth 0 (root)
   *   "1.10"    → depth 1, parent "1"
   *   "1.10.1"  → depth 2, parent "1.10"
   * No maximum depth is enforced.
   *
   * routePath is taken directly from the API response — the ONLY allowed
   * source of Angular navigation paths. No ROUTE_MAP, no name-based lookup.
   */
  private buildTree(items: MenuItemDto[]): MenuNode[] {
    const nodeMap = new Map<string, MenuNode>();
    const roots: MenuNode[] = [];

    for (const item of items) {
      const node: MenuNode = {
        ...item,
        // routePath comes from the API (MENUS.RoutePath from the backend).
        // Keep it as-is: null means this is a parent/group-only node.
        routePath: item.routePath ?? null,
        children:  [],
        expanded:  false,
      };
      nodeMap.set(item.menuId, node);

      const parentId = this.parentMenuId(item.menuId);
      if (!parentId) {
        roots.push(node);
      } else {
        const parent = nodeMap.get(parentId);
        if (parent) {
          parent.children.push(node);
        } else {
          // Defensive: parent not found — treat as root.
          roots.push(node);
        }
      }
    }

    return roots;
  }

  /**
   * Returns the immediate parent MenuId by trimming the last dot segment.
   * "1.10.1" → "1.10"  |  "1.10" → "1"  |  "1" → null
   */
  private parentMenuId(menuId: string): string | null {
    const dot = menuId.lastIndexOf('.');
    return dot > 0 ? menuId.slice(0, dot) : null;
  }
}
